// app/javascript/controllers/invitation_animation_controller.js
import { Controller } from "@hotwired/stimulus"
import { gsap } from "gsap"

export default class extends Controller {
    static values = {
        actionUrl: String,
        invitationId: Number,
        animated: Boolean,
        enabled: Boolean,
        status: String,
        videoUrl: String,
        soundEnabled: Boolean,
        draftId: Number,
        partyId: Number
    }

    static targets = [
        "toggleBtn", "label", "spinner", "hint",
        "soundBtn", "soundLabel", "soundIcon",
        "waitingText", "waitingStep", "magicEmoji", "waitingTip", "magicLottie"
    ]

    connect() {
        const invId = this._invitationId()
        this._soundStorageKey = invId ? `invitation_sound_${invId}` : null

        // Restore waiting started timestamp from sessionStorage if present
        this._waitingStorageKey = invId ? `invitation_waiting_started_${invId}` : null
        const existingStarted = this._waitingStorageKey ? window.sessionStorage.getItem(this._waitingStorageKey) : null
        if (existingStarted) {
            const n = parseInt(existingStarted, 10)
            if (!Number.isNaN(n) && n > 0) this._waitingStartedAt = n
        }

        // MVP UX: always default sound to OFF (do not persist ON across reloads).
        this.soundEnabledValue = false
        if (this._soundStorageKey) {
            window.localStorage.setItem(this._soundStorageKey, "off")
        }

        this._renderSoundUI()
        this._refreshSoundButtonState()
        this._syncWaitingUI()
        // When loading a previously-generated animation, make sure the editor is synced
// with the server-rendered enabled state, otherwise CTA can say "Turn off"
// while editor still shows the static image.
        if (invId && this.animatedValue && !this._isBusy()) {
            window.dispatchEvent(new CustomEvent("invitation:animationEnabledChanged", {
                detail: {
                    invitationId: invId,
                    enabled: !!this.enabledValue,
                    draftId: (this.hasDraftIdValue ? this.draftIdValue : null),
                    partyId: (this.hasPartyIdValue ? this.partyIdValue : null)
                }
            }))
        }
        if (!this._isBusy()) this._stopWaitingLottie()
    }

    async startOrToggle(event) {
        // Prevent any default button/form/Turbo behavior from firing alongside our fetch.
        event?.preventDefault?.()
        event?.stopPropagation?.()

        if (this.animatedValue) {
            return this.toggleEnabled(event)
        }
        return this.startGeneration()
    }

    async startGeneration() {
        this.setBusy(true)
        this.setHint("Starting animation…")

        // Store waiting started timestamp in sessionStorage
        const invId = this._invitationId()
        if (invId) {
            this._waitingStorageKey = this._waitingStorageKey || `invitation_waiting_started_${invId}`
            try { window.sessionStorage.setItem(this._waitingStorageKey, String(Date.now())) } catch (_) {}
        }

        const body = {}
        if (this.hasDraftIdValue && this.draftIdValue) body.draft_id = this.draftIdValue
        if (this.hasPartyIdValue && this.partyIdValue) body.party_id = this.partyIdValue

        const res = await fetch(this.actionUrlValue, {
            method: "POST",
            headers: this.headers(),
            body: JSON.stringify(body)
        })

        if (!res.ok) {
            const data = await res.json().catch(() => ({}))
            this.setBusy(false)
            this.setHint(data.error || data.message || "Could not start animation.")
            return
        }

        // Apply Turbo Stream from response if present.
        await this._applyTurboStreamFromResponse(res)

        // After successful Turbo Stream, set enabledValue true and notify editor
        this.enabledValue = true

        window.dispatchEvent(new CustomEvent("invitation:animationEnabledChanged", {
            detail: {
                invitationId: this._invitationId(),
                enabled: true,
                draftId: (this.hasDraftIdValue ? this.draftIdValue : null),
                partyId: (this.hasPartyIdValue ? this.partyIdValue : null)
            }
        }))

        // No need to poll — Turbo Stream broadcast will update the CTA.
        // Set local status so the waiting UI kicks in immediately.
        this.statusValue = "processing"
        this._refreshSoundButtonState()
        this._syncWaitingUI()
    }

    async toggleEnabled(event) {
        // Prevent any default action (Turbo form submission, etc.)
        event?.preventDefault?.()
        event?.stopPropagation?.()

// Decide desired state from the button label so we don't get stuck
// when the draft/party flag defaults to false on initial render.
        const btnText = (this.hasToggleBtnTarget ? (this.toggleBtnTarget.textContent || "") : "").toLowerCase()

        let desiredEnabled
        if (btnText.includes("turn on") || btnText.includes("make it move") || btnText.includes("enable")) {
            desiredEnabled = true
        } else if (btnText.includes("turn off") || btnText.includes("disable")) {
            desiredEnabled = false
        } else {
            // Fallback: toggle based on the most reliable current state.
            const dsVal = this.element?.dataset?.invitationAnimationEnabledValue
            const currentEnabled = (dsVal === undefined || dsVal === null || dsVal === "")
                ? !!this.enabledValue
                : (dsVal === "true" || dsVal === true)
            desiredEnabled = !currentEnabled
        }

        this.setBusy(true)

        const body = { enabled: desiredEnabled }
        if (this.hasDraftIdValue && this.draftIdValue) body.draft_id = this.draftIdValue
        if (this.hasPartyIdValue && this.partyIdValue) body.party_id = this.partyIdValue

        const res = await fetch(this.actionUrlValue, {
            method: "PATCH",
            headers: this.headers(),
            body: JSON.stringify(body)
        })

        this.setBusy(false)

        if (!res.ok) {
            const data = await res.json().catch(() => ({}))
            this.setHint(data.error || "Could not update setting.")
            return
        }

        // Apply Turbo Stream from response if present.
        await this._applyTurboStreamFromResponse(res)

        // Optimistically update local values (CTA will also re-render via Turbo Stream broadcast).
        this.enabledValue = desiredEnabled

        // UX: turning animation on/off should not implicitly keep sound on.
        this.soundEnabledValue = false
        if (this._soundStorageKey) {
            window.localStorage.setItem(this._soundStorageKey, "off")
        }

        // Ensure editor/video is muted too.
        window.dispatchEvent(new CustomEvent("invitation:animationSoundToggled", {
            detail: {
                invitationId: this._invitationId(),
                enabled: false
            }
        }))

        this._setDefaultHint()

        this._renderSoundUI()
        this._refreshSoundButtonState()
        this._syncWaitingUI()
    }

    headers(withJson = true) {
        const token = document.querySelector("meta[name='csrf-token']")?.content
        const h = { Accept: "text/vnd.turbo-stream.html, application/json", "X-Requested-With": "XMLHttpRequest" }
        if (withJson) h["Content-Type"] = "application/json"
        if (token) h["X-CSRF-Token"] = token
        return h
    }
    // Prefer Turbo Stream responses so the CTA updates even if ActionCable messages are missed.
    _applyTurboStreamFromResponse(res, fallbackJsonHandler = null) {
        const ct = (res.headers.get("content-type") || "").toLowerCase()

        if (ct.includes("text/vnd.turbo-stream.html")) {
            return res.text().then((html) => {
                if (html && window.Turbo && typeof window.Turbo.renderStreamMessage === "function") {
                    window.Turbo.renderStreamMessage(html)
                }
            })
        }

        if (typeof fallbackJsonHandler === "function") {
            return res.json().catch(() => ({})).then(fallbackJsonHandler)
        }

        return Promise.resolve(null)
    }


    setBusy(isBusy) {
        if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("d-none", !isBusy)
        if (this.hasToggleBtnTarget) this.toggleBtnTarget.disabled = isBusy
        this._refreshSoundButtonState()
        this._syncWaitingUI()
    }

    setHint(text) {
        // The CTA subtitle can contain nested spans (waitingText/magicEmoji/waitingStep).
        // Never clobber the entire hint container, otherwise we lose those elements.
        if (this.hasWaitingTextTarget) {
            this.waitingTextTarget.textContent = text
            return
        }

        // Fallback for non-busy CTA renders where waiting spans may not exist.
        if (this.hasHintTarget) this.hintTarget.textContent = text
    }

    _setDefaultHint() {
        if (this.animatedValue) {
            this.setHint(this.enabledValue ? "Animation is ON." : "Animation is OFF.")
        } else {
            this.setHint("Generate a short looping animated version of this invite.")
        }
    }

    toggleDebugWaiting(event) {
        event?.preventDefault?.()

        // Toggle an internal flag
        this._debugWaiting = !this._debugWaiting

        if (this._debugWaiting) {
            // Pretend we're busy
            this.statusValue = "processing"
            if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("d-none")
        } else {
            // Stop pretending
            this.statusValue = "ready"
            // Restore a sensible subtitle
            this._setDefaultHint()
            if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("d-none")
        }

        this._syncWaitingUI()
    }

    toggleSound(event) {
        // NOTE: This handler may be bound to pointerdown for stricter autoplay policies.
        // Keep it synchronous: do not await anything in here.
        event?.preventDefault?.()

        // Only meaningful if animation is currently allowed and we have a generated video.
        if (!this._soundAllowed()) return

        const next = !this.soundEnabledValue
        this.soundEnabledValue = next

        if (this._soundStorageKey) {
            window.localStorage.setItem(this._soundStorageKey, next ? "on" : "off")
        }

        this._renderSoundUI()
        this._refreshSoundButtonState()

        const invId = this._invitationId()

        // 0) BEST: directly target the editor video element and unmute+play within this gesture.
        // This avoids losing user-activation context through CustomEvent/callback hops.
        try {
            const v = document.querySelector('[data-invitation-editor-target="bgVideo"]')
            if (v) {
                if (!next) {
                    try { v.muted = true } catch (_) {}
                    try { v.setAttribute("muted", "") } catch (_) {}
                } else {
                    try { v.muted = false } catch (_) {}
                    try { v.removeAttribute("muted") } catch (_) {}
                    try { v.volume = 1 } catch (_) {}

                    // Re-trigger play after unmuting so audio applies immediately.
                    try { v.pause?.() } catch (_) {}
                    const p = v.play?.()
                    if (p && typeof p.catch === "function") {
                        p.catch(() => {
                            // If still blocked, leave muted and let editor defer to next gesture.
                            try { v.muted = true } catch (_) {}
                            try { v.setAttribute("muted", "") } catch (_) {}
                        })
                    }
                }
            }
        } catch (_) {}

        // 1) Also invoke the editor callback if present (harmless; helps if selector changes)
        try {
            const cb = window.EchoageInvitationSoundCallbacks && window.EchoageInvitationSoundCallbacks[String(invId)]
            if (typeof cb === "function") cb(next)
        } catch (_) {}

        // 2) Fallback: notify other pages (RSVP/preview) via event
        window.dispatchEvent(new CustomEvent("invitation:animationSoundToggled", {
            detail: {
                invitationId: invId,
                enabled: next
            }
        }))
    }

    _renderSoundUI() {
        const on = !!this.soundEnabledValue

        if (this.hasSoundLabelTarget) {
            this.soundLabelTarget.textContent = on ? "Sound on" : "Sound off"
        }

        if (this.hasSoundIconTarget) {
            this.soundIconTarget.innerHTML = on
                ? '<i class="bi bi-volume-up-fill"></i>'
                : '<i class="bi bi-volume-mute-fill"></i>'
        }
    }

    _soundAllowed() {
        // Sound only makes sense when animation is ON, generated, and not busy.
        const enabled = (this.enabledValue === undefined) ? true : !!this.enabledValue
        const animated = !!this.animatedValue
        const hasVideo = !!this.videoUrlValue

        // treat nil status as ok; also accept done
        const readyish = !this.statusValue || this.statusValue === "ready" || this.statusValue === "done"

        const busy = this.hasSpinnerTarget && !this.spinnerTarget.classList.contains("d-none")

        return enabled && animated && hasVideo && readyish && !busy
    }

    _refreshSoundButtonState() {
        if (!this.hasSoundBtnTarget) return

        const allowed = this._soundAllowed()
        this.soundBtnTarget.disabled = !allowed

        if (!allowed) {
            this.soundBtnTarget.title = "Turn on animation to enable sound"
        } else {
            this.soundBtnTarget.removeAttribute("title")
        }
    }

    _lottieStepName(idx) {
        // Keep these names in sync with your local files in assets/lotties/*.json
        switch (idx) {
            case 0: return "wand"
            case 1: return "dancing"
            case 2: return "motion"
            case 3: return "polish"
            case 4: return "final"
            default: return "wand"
        }
    }

    _waitingLottieController() {
        if (!this.hasMagicLottieTarget) return null
        try {
            return this.application.getControllerForElementAndIdentifier(this.magicLottieTarget, "waiting-lottie")
        } catch (_) {
            return null
        }
    }

    _setWaitingLottie(idx) {
        if (!this.hasMagicLottieTarget) return

        const name = this._lottieStepName(idx)
        const ctrl = this._waitingLottieController()

        // Preferred: the span has its own waiting-lottie controller.
        if (ctrl && typeof ctrl.play === "function") {
            ctrl.play(name)
            return
        }

        // Fallback: if the span doesn't have a controller yet, set it up automatically.
        // This makes it work even if you forgot `data-controller="waiting-lottie"`.
        try {
            this.magicLottieTarget.setAttribute("data-controller", "waiting-lottie")
            this.magicLottieTarget.setAttribute("data-waiting-lottie-name-value", name)
        } catch (_) {}

        // Stimulus will connect it on the next microtask.
        queueMicrotask(() => {
            const c2 = this._waitingLottieController()
            if (c2 && typeof c2.play === "function") c2.play(name)
        })
    }

    _stopWaitingLottie() {
        if (!this.hasMagicLottieTarget) return
        const ctrl = this._waitingLottieController()
        if (ctrl && typeof ctrl.stop === "function") {
            ctrl.stop()
            return
        }

        // If no controller, just clear the element.
        try { this.magicLottieTarget.innerHTML = "" } catch (_) {}
    }

    _isBusy() {
        if (this._debugWaiting) return true
        const s = (this.statusValue || "").toString()
        return s === "queued" || s === "processing"
    }

    _isDev() {
        try {
            // Works with esbuild/webpack style envs
            if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
                return process.env.NODE_ENV === "development"
            }
        } catch (_) {}

        // Fallback: common local hosts
        const host = (window?.location?.hostname || "").toLowerCase()
        return host === "localhost" || host.endsWith(".local") || host === "127.0.0.1" || host == "[::1]"
    }

    _syncWaitingUI() {
        const busy = this._isBusy()
        // Allow CSS to adapt CTA layout when waiting UI/tips are shown.
        try { this.element.classList.toggle("is-busy", busy) } catch (_) {}

        if (busy) {
            this._startWaitingFun()
            this._startTipsRotation()
        } else {
            this._stopWaitingFun({ preserveStorage: false })
            this._stopTipsRotation()
        }

        if (this.hasWaitingTipTarget) {
            this.waitingTipTarget.classList.toggle("d-none", !busy)
        }
    }

    _startWaitingFun() {
        if (this._waitingInterval) return

        // In dev preview, the partial might not have the waiting spans.
        // Fall back to the normal subtitle (`hintTarget`) so we can still preview copy.
        const textEl = this.hasWaitingTextTarget ? this.waitingTextTarget : (this.hasHintTarget ? this.hintTarget : null)
        if (!textEl) return

        // --- Stabilize layout for waiting status text (prevent CTA box jumping) ---
        // Measure the tallest possible waiting message once and lock min-height.
        // This avoids reflow when different-length messages swap.
        try {
            if (!this._waitingTextMinHeightLocked) {
                // Ensure our messages exist before measurement.
                const msgs = (this._waitingMessages || []).length ? this._waitingMessages : [
                    "Warming up the magic wand…",
                    "Imagining the motion…",
                    "Teaching the invite to dance…",
                    "Polishing the details…",
                    "Final touches… almost there!"
                ]

                // Temporarily inject a measurer span.
                const measurer = document.createElement("span")
                measurer.style.position = "absolute"
                measurer.style.left = "-99999px"
                measurer.style.top = "-99999px"
                measurer.style.whiteSpace = "normal"
                measurer.style.visibility = "hidden"

                // Match typography/wrapping to the target container.
                const cs = window.getComputedStyle(textEl)
                measurer.style.font = cs.font
                measurer.style.fontSize = cs.fontSize
                measurer.style.fontFamily = cs.fontFamily
                measurer.style.fontWeight = cs.fontWeight
                measurer.style.letterSpacing = cs.letterSpacing
                measurer.style.lineHeight = cs.lineHeight

                // Constrain width to the current container width so wrapping matches.
                const w = Math.ceil(textEl.getBoundingClientRect().width)
                if (w > 0) measurer.style.width = `${w}px`

                document.body.appendChild(measurer)

                let maxH = 0
                msgs.forEach((m) => {
                    measurer.textContent = m
                    maxH = Math.max(maxH, measurer.getBoundingClientRect().height)
                })

                measurer.remove()

                if (maxH > 0) {
                    textEl.style.display = textEl.style.display || "inline-block"
                    textEl.style.position = textEl.style.position || "relative"
                    textEl.style.minHeight = `${Math.ceil(maxH)}px`
                }

                this._waitingTextMinHeightLocked = true
            }
        } catch (_) {}

        const animateWaitingTextSwap = (newText, onIncomingStart = null) => {
            try {
                const el = textEl

                // Fade the whole "waiting row" together: message text + step badge + lottie.
                const fadeEls = [el]
                if (this.hasWaitingStepTarget) fadeEls.push(this.waitingStepTarget)
                if (this.hasMagicLottieTarget) fadeEls.push(this.magicLottieTarget)

                // Kill any in-flight animations to avoid flashing.
                try { gsap.killTweensOf(fadeEls) } catch (_) {}

                const applyText = () => {
                    if (el === this.hintTarget) {
                        this.setHint(newText)
                    } else {
                        el.textContent = newText
                    }
                }

                // Keep a stable baseline (avoid stuck invisibility on rapid toggles)
                try { gsap.set(fadeEls, { opacity: 1 }) } catch (_) {}

                // Slower, smoother: fade out -> swap -> fade in.
                const tl = gsap.timeline({ defaults: { ease: "power2.out", overwrite: "auto" } })

                // Don’t drop to 0 instantly; keep it readable a bit longer.
                tl.to(fadeEls, { duration: 0.45, opacity: 0 }, 0)

                // Swap text while hidden.
                tl.add(() => {
                    applyText()
                }, ">")

                // Sync lottie change with the start of the fade-in (so it never changes while visible).
                if (typeof onIncomingStart === "function") {
                    tl.add(() => {
                        try { onIncomingStart() } catch (_) {}
                    }, ">")
                }

                tl.to(fadeEls, { duration: 0.65, opacity: 1 }, ">")

                // Clean up any inline opacity that might fight CSS later.
                tl.add(() => {
                    try { gsap.set(fadeEls, { clearProps: "opacity" }) } catch (_) {}
                }, ">")
            } catch (_) {
                // Fallback: immediate swap
                if (textEl === this.hintTarget) {
                    this.setHint(newText)
                } else {
                    textEl.textContent = newText
                }

                try { onIncomingStart && onIncomingStart() } catch (_) {}
            }
        }

        // Ensure the element starts visible to prevent opacity staying at 0 after a fast toggle.
        try { gsap.set(textEl, { opacity: 1 }) } catch (_) {}

        this._waitingStartedAt ||= Date.now()

        this._waitingMessages = [
            "Warming up the magic wand…",
            "Imagining the motion…",
            "Teaching the invite to dance…",
            "Polishing the details…",
            "Final touches… almost there!"
        ]

        this._waitingEmojis = ["✨", "🪄", "🎉", "💫", "🌟"]

        // On reconnect (Turbo replace), don’t jump back to the first message.
        // Render the message that matches elapsed time since waiting started.
        const elapsed0 = Date.now() - this._waitingStartedAt
        const idx0 = Math.min(4, Math.floor(elapsed0 / 10000))

        // Time-based messages: advance every 10s, then stay on the final message.
        this._waitingLastIndex = this._waitingLastIndex ?? -1

        this._waitingInterval = window.setInterval(() => {
            const elapsed = Date.now() - this._waitingStartedAt

            // 0-10s: 0, 10-20s: 1, 20-30s: 2, 30-40s: 3, 40s+: 4 (final)
            const idx = Math.min(4, Math.floor(elapsed / 10000))

            // Only update DOM when the message actually changes.
            if (idx !== this._waitingLastIndex) {
                const emoji = this._waitingEmojis[idx % this._waitingEmojis.length]
                if (this.hasMagicEmojiTarget) {
                    try { this.magicEmojiTarget.textContent = "" } catch (_) {}
                }

                if (this.hasMagicEmojiTarget) {
                    animateWaitingTextSwap(this._waitingMessages[idx], () => this._setWaitingLottie(idx))
                } else {
                    const msg = `${emoji} ${this._waitingMessages[idx]}`
                    animateWaitingTextSwap(msg, () => this._setWaitingLottie(idx))
                }

                if (this.hasWaitingStepTarget) {
                    // Keep the 1/3 -> 2/3 -> 3/3 indicator in sync with time.
                    const step = elapsed < 12000 ? 1 : (elapsed < 28000 ? 2 : 3)
                    try { this.waitingStepTarget.textContent = `${step}/3` } catch (_) {}
                }

                this._waitingLastIndex = idx
            } else if (this.hasWaitingStepTarget) {
                // Still update the step badge even if the message didn't change.
                const step = elapsed < 12000 ? 1 : (elapsed < 28000 ? 2 : 3)
                try { this.waitingStepTarget.textContent = `${step}/3` } catch (_) {}
            }
        }, 500)

        // Set an immediate message (no animation on first render), based on elapsed time.
        const firstEmoji = this._waitingEmojis[idx0 % this._waitingEmojis.length]

        if (this.hasMagicEmojiTarget) {
            try { this.magicEmojiTarget.textContent = "" } catch (_) {}
        }

        const firstMsg = this.hasMagicEmojiTarget
            ? this._waitingMessages[idx0]
            : `${firstEmoji} ${this._waitingMessages[idx0]}`

        // Render text immediately
        if (textEl === this.hintTarget) {
            this.setHint(firstMsg)
        } else {
            textEl.textContent = firstMsg
        }

        // Set and show lottie in sync with the visible text
        this._setWaitingLottie(idx0)
        if (this.hasMagicLottieTarget) {
            this.magicLottieTarget.classList.remove("d-none")
        }

        // Avoid the next interval tick re-applying the same message.
        this._waitingLastIndex = idx0

        // Step badge should match elapsed time too.
        if (this.hasWaitingStepTarget) {
            const step0 = elapsed0 < 12000 ? 1 : (elapsed0 < 28000 ? 2 : 3)
            try { this.waitingStepTarget.textContent = `${step0}/3` } catch (_) {}
        }
    }

    _startTipsRotation() {
        if (this._tipsInterval) return
        if (!this.hasWaitingTipTarget) return

        const ul = this.waitingTipTarget
        const items = Array.from(ul.querySelectorAll("li"))
        if (items.length === 0) return

        // Prevent layout jumps when different-length tips rotate.
        // Measure the tallest tip once and lock the container height.
        try {
            const prevDisplay = items.map(li => li.style.display)
            const prevVisibility = items.map(li => li.style.visibility)

            items.forEach(li => {
                li.style.display = "list-item"
                li.style.visibility = "hidden"
            })

            let maxH = 0
            items.forEach(li => {
                maxH = Math.max(maxH, li.getBoundingClientRect().height)
            })

            items.forEach((li, idx) => {
                li.style.display = prevDisplay[idx] || ""
                li.style.visibility = prevVisibility[idx] || ""
            })

            if (maxH > 0) ul.style.minHeight = `${Math.ceil(maxH)}px`
        } catch (_) {}

        // Show only one tip at a time, without reflow: stack items on top of each other.
        // Using absolute positioning + GSAP autoAlpha prevents the container from jumping.
        try {
            ul.style.position = "relative"
            ul.style.paddingLeft = "0"
            ul.style.listStyle = "none"
        } catch (_) {}

        this._tipIndex = (this._tipIndex || 0) % items.length
        items.forEach((li, idx) => {
            try {
                li.style.position = "absolute"
                li.style.left = "0"
                li.style.top = "0"
                li.style.width = "100%"
                li.style.margin = "0"
            } catch (_) {}

            // Keep bullets from affecting layout when absolute; safest is to hide list markers.
            try { li.style.listStyleType = "none" } catch (_) {}

            if (idx === this._tipIndex) {
                li.style.visibility = "visible"
                li.style.opacity = "1"
                li.style.transform = "translateX(0)"
            } else {
                li.style.visibility = "hidden"
                li.style.opacity = "0"
                li.style.transform = "translateX(0)"
            }
        })

        // Rotate every 6 seconds.
        this._tipsInterval = window.setInterval(() => {
            if (!this.hasWaitingTipTarget) return

            const currentItems = Array.from(this.waitingTipTarget.querySelectorAll("li"))
            if (currentItems.length === 0) return

            const currentIndex = (this._tipIndex || 0) % currentItems.length
            const nextIndex = (currentIndex + 1) % currentItems.length

            const current = currentItems[currentIndex]
            const next = currentItems[nextIndex]

            // Prepare next (keep layout stable; just toggle visibility/opacity)
            next.style.visibility = "visible"
            next.style.opacity = "0"
            next.style.transform = "translateX(18px)"

            // Animate out current, animate in next (crossfade + slide horizontally)
            try {
                const tl = gsap.timeline({ defaults: { ease: "power1.out" } })

                tl.to(current, {
                    duration: 0.25,
                    opacity: 0,
                    x: -18
                }, 0)

                tl.to(next, {
                    duration: 0.35,
                    opacity: 1,
                    x: 0
                }, 0)

                tl.add(() => {
                    // After transition: hide the old one to keep it out of the way.
                    current.style.visibility = "hidden"
                    current.style.transform = "translateX(0)"
                })
            } catch (_) {
                // Fallback without gsap
                current.style.visibility = "hidden"
                current.style.opacity = "0"
                current.style.transform = "translateX(0)"

                next.style.visibility = "visible"
                next.style.opacity = "1"
                next.style.transform = "translateX(0)"
            }

            this._tipIndex = nextIndex
        }, 6000)
    }

    _stopTipsRotation() {
        if (this._tipsInterval) {
            window.clearInterval(this._tipsInterval)
            this._tipsInterval = null
        }
        this._tipIndex = 0

        if (this.hasWaitingTipTarget) {
            try { this.waitingTipTarget.style.minHeight = "" } catch (_) {}
            const items = Array.from(this.waitingTipTarget.querySelectorAll("li"))
            items.forEach((li) => {
                li.style.display = "list-item"
                li.style.opacity = ""
                li.style.transform = ""
            })
        }
    }

    _stopWaitingFun({ preserveStorage = false } = {}) {
        if (this._waitingInterval) {
            window.clearInterval(this._waitingInterval)
            this._waitingInterval = null
        }

        if (this.hasMagicLottieTarget) this.magicLottieTarget.classList.add("d-none")

        // Only clear the saved start time when we're truly done (not when Turbo replaces the node)
        if (!preserveStorage && this._waitingStorageKey) {
            try { window.sessionStorage.removeItem(this._waitingStorageKey) } catch (_) {}
            this._waitingStartedAt = null
        }

        this._waitingLastIndex = -1
        this._stopWaitingLottie()
    }

    _invitationId() {
        // Prefer the value if present; fall back to parsing it from the action URL.
        const fromValue = this.invitationIdValue
        if (fromValue) return fromValue

        const url = (this.actionUrlValue || "").toString()
        const m = url.match(/\/invitations\/(\d+)\//)
        return m ? m[1] : null
    }

    disconnect() {
        this._debugWaiting = false

        // If we're still queued/processing, Turbo replacement should NOT wipe the stored timestamp.
        const preserve = this._isBusy()
        this._stopWaitingFun({ preserveStorage: preserve })

        this._stopTipsRotation()
    }
}